import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { toast } from 'sonner';
import { ChevronDown, Copy, Search, Users, XCircle } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { BookingStatusBadge } from './BookingStatusBadge';
import { useEventBookings } from '@/hooks/useEventBookings';
import { supabase } from '@/integrations/supabase/client';
import { captureAppError } from '@/lib/monitoring/sentry';
import { logSystemEvent, generateRequestId } from '@/lib/observability/logSystemEvent';
import { buildSystemEventContext } from '@/lib/observability/buildSystemEventContext';
import {
  groupProducerBookings,
  filterBookingGroups,
  getBookingCode,
  type ProducerBookingGroup,
} from '@/lib/groupProducerBookings';
import type { EventBooking, BookingStatus } from '@/types/events';

interface EventBookingsListProps {
  eventId: string;
  maxSpots: number;
}

export function EventBookingsList({ eventId, maxSpots }: EventBookingsListProps) {
  const { data: bookings, isLoading, error } = useEventBookings(eventId);
  const queryClient = useQueryClient();
  const [isCancellingHost, setIsCancellingHost] = useState(false);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState<EventBooking | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Calculate summary from raw bookings
  const confirmedBookings = bookings?.filter((b) => b.status === 'confirmed') || [];
  const bookedSpots = confirmedBookings.reduce((sum, b) => sum + b.spots, 0);
  const confirmedCount = confirmedBookings.length;

  // Group and filter
  const groups = useMemo(() => {
    if (!bookings) return [];
    return groupProducerBookings(bookings);
  }, [bookings]);

  const filteredGroups = useMemo(
    () => filterBookingGroups(groups, searchQuery),
    [groups, searchQuery]
  );

  const handleCancelClick = (booking: EventBooking) => {
    setBookingToCancel(booking);
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!bookingToCancel) return;

    setIsCancellingHost(true);
    const requestId = generateRequestId();
    try {
      const { data, error: fnError } = await supabase.functions.invoke('cancel-event-booking', {
        body: { token: bookingToCancel.confirmation_token },
      });

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      toast.success('Bokningen är avbokad');
      queryClient.invalidateQueries({ queryKey: ['event-bookings', eventId] });
      queryClient.invalidateQueries({ queryKey: ['public-event', eventId] });
      queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
    } catch (err) {
      console.error('Failed to cancel booking:', err);
      toast.error('Kunde inte avboka bokningen. Försök igen.');

      captureAppError(err, {
        feature: 'booking',
        action: 'host_cancel',
        event_id: eventId,
        booking_id: bookingToCancel.id,
        request_id: requestId,
      });
      logSystemEvent({
        level: 'error',
        event_key: 'host_cancel_booking_failed',
        entity_type: 'booking',
        entity_id: bookingToCancel.id,
        message: `Host cancel booking failed: ${err instanceof Error ? err.message : 'unknown'}`,
        request_id: requestId,
        context: buildSystemEventContext({ feature: 'booking', action: 'host_cancel', surface: 'host_bookings_list', event_id: eventId }),
      });
    } finally {
      setIsCancellingHost(false);
      setCancelDialogOpen(false);
      setBookingToCancel(null);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Bokningar</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive">
            Kunde inte ladda bokningar. Försök igen senare.
          </p>
        </CardContent>
      </Card>
    );
  }

  const hasBookings = bookings && bookings.length > 0;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Bokningar
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Summary */}
          <div
            data-testid="producer-bookings-summary"
            className="rounded-lg bg-muted p-4"
          >
            <p className="text-sm font-medium">
              {bookedSpots} av {maxSpots} platser bokade ({confirmedCount}{' '}
              {confirmedCount === 1 ? 'bokning' : 'bokningar'})
            </p>
          </div>

          {/* Search */}
          {hasBookings && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Sök namn, e-post eller bokningskod…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          )}

          {/* Bookings table or empty state */}
          {hasBookings ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Namn</TableHead>
                    <TableHead>E-post</TableHead>
                    <TableHead className="text-center">Platser</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Bokningskod</TableHead>
                    <TableHead>Bokad</TableHead>
                    <TableHead className="text-right">Åtgärder</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredGroups.length === 0 && searchQuery ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-6 text-muted-foreground">
                        Inga bokningar matchar "{searchQuery}"
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredGroups.map((group) => (
                      <BookingGroupRows
                        key={group.key}
                        group={group}
                        onCancelClick={handleCancelClick}
                        isCancelling={isCancellingHost}
                        searchQuery={searchQuery}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground">Inga bokningar ännu</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent data-testid="producer-booking-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Avboka bokning</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill markera denna bokning som avbokad?
              {bookingToCancel && (
                <span className="block mt-2 font-medium">
                  {bookingToCancel.guest_name || bookingToCancel.guest_email} –{' '}
                  {bookingToCancel.spots}{' '}
                  {bookingToCancel.spots === 1 ? 'plats' : 'platser'}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancel}
              data-testid="producer-booking-cancel-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Avboka
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/* ── Group rows ── */

interface BookingGroupRowsProps {
  group: ProducerBookingGroup;
  onCancelClick: (booking: EventBooking) => void;
  isCancelling: boolean;
  searchQuery: string;
}

function BookingGroupRows({
  group,
  onCancelClick,
  isCancelling,
  searchQuery,
}: BookingGroupRowsProps) {
  const isSingle = group.items.length === 1;
  // Auto-expand when search matches a booking code inside the group
  const [isOpen, setIsOpen] = useState(false);

  const shouldAutoExpand = useMemo(() => {
    if (!searchQuery.trim() || isSingle) return false;
    const q = searchQuery.trim().toLowerCase();
    return group.items.some((item) =>
      getBookingCode(item).toLowerCase().includes(q)
    );
  }, [searchQuery, group.items, isSingle]);

  const effectiveOpen = isOpen || shouldAutoExpand;

  if (isSingle) {
    const booking = group.items[0];
    return (
      <TableRow className={booking.status === 'cancelled' ? 'opacity-50' : ''}>
        <TableCell className="font-medium">{booking.guest_name || '–'}</TableCell>
        <TableCell>{booking.guest_email}</TableCell>
        <TableCell className="text-center">{booking.spots}</TableCell>
        <TableCell>
          <BookingStatusBadge status={booking.status} />
        </TableCell>
        <TableCell>
          <BookingCodeCell code={getBookingCode(booking)} />
        </TableCell>
        <TableCell>
          {format(new Date(booking.created_at), 'd MMM yyyy, HH:mm', { locale: sv })}
        </TableCell>
        <TableCell className="text-right">
          <BookingActions
            booking={booking}
            onCancelClick={onCancelClick}
            isCancelling={isCancelling}
          />
        </TableCell>
      </TableRow>
    );
  }

  // Multi-booking group
  return (
    <Collapsible open={effectiveOpen} onOpenChange={setIsOpen} asChild>
      <>
        {/* Group summary row */}
        <TableRow className={group.status === 'cancelled' ? 'opacity-50' : ''}>
          <TableCell className="font-medium">{group.personName || '–'}</TableCell>
          <TableCell>{group.personEmail}</TableCell>
          <TableCell className="text-center">{group.totalSpots}</TableCell>
          <TableCell>
            <BookingStatusBadge status={group.status as BookingStatus} />
          </TableCell>
          <TableCell>
            <span className="text-sm text-muted-foreground">
              {group.items.length} koder
            </span>
          </TableCell>
          <TableCell>
            {format(new Date(group.latestBookedAt), 'd MMM yyyy, HH:mm', { locale: sv })}
          </TableCell>
          <TableCell className="text-right">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm">
                <span className="mr-1 text-xs">Hantera ({group.items.length})</span>
                <ChevronDown className="h-4 w-4 transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
              </Button>
            </CollapsibleTrigger>
          </TableCell>
        </TableRow>

        {/* Expanded sub-rows */}
        <CollapsibleContent asChild>
          <>
            {group.items.map((booking) => (
              <TableRow
                key={booking.id}
                className={`bg-muted/30 ${booking.status === 'cancelled' ? 'opacity-50' : ''}`}
              >
                <TableCell className="pl-8 text-muted-foreground text-xs" colSpan={2}>
                  ↳ Bokning
                </TableCell>
                <TableCell className="text-center">{booking.spots}</TableCell>
                <TableCell>
                  <BookingStatusBadge status={booking.status} />
                </TableCell>
                <TableCell>
                  <BookingCodeCell code={getBookingCode(booking)} />
                </TableCell>
                <TableCell>
                  {format(new Date(booking.created_at), 'd MMM yyyy, HH:mm', { locale: sv })}
                </TableCell>
                <TableCell className="text-right">
                  <BookingActions
                    booking={booking}
                    onCancelClick={onCancelClick}
                    isCancelling={isCancelling}
                  />
                </TableCell>
              </TableRow>
            ))}
          </>
        </CollapsibleContent>
      </>
    </Collapsible>
  );
}

/* ── Booking code cell ── */

function BookingCodeCell({ code }: { code: string }) {

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success('Bokningskod kopierad');
    } catch {
      // Fallback: select text
    }
  };

  return (
    <span className="inline-flex items-center gap-1">
      <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{code}</code>
      <button
        type="button"
        onClick={handleCopy}
        className="text-muted-foreground hover:text-foreground transition-colors"
        title="Kopiera bokningskod"
      >
        <Copy className="h-3 w-3" />
      </button>
    </span>
  );
}

/* ── Booking actions ── */

function BookingActions({
  booking,
  onCancelClick,
  isCancelling,
}: {
  booking: EventBooking;
  onCancelClick: (b: EventBooking) => void;
  isCancelling: boolean;
}) {
  if (booking.status === 'confirmed') {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onCancelClick(booking)}
        disabled={isCancelling}
      >
        <XCircle className="h-4 w-4 mr-1" />
        Avboka
      </Button>
    );
  }

  if (booking.status === 'cancelled') {
    return (
      <span className="text-xs text-muted-foreground">
        Avbokad{' '}
        {booking.cancelled_at
          ? format(new Date(booking.cancelled_at), 'd MMM', { locale: sv })
          : ''}
      </span>
    );
  }

  return null;
}
